import { useEffect } from 'react';
import { XIcon } from './Icons';

/**
 * What the app does and where to find it.
 *
 * Written because page numbers and watermarks live inside the export dialog,
 * which is the right place to configure them and the wrong place to discover
 * them. Kept short on purpose: a wall of text is read by nobody.
 */
export function HelpDialog({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="dialog help" role="dialog" aria-modal="true" aria-label="Help">
        <button className="icon-btn help__close" onClick={onClose} aria-label="Close help">
          <XIcon />
        </button>

        <h2>Using PDF Toolkit</h2>
        <p className="dialog__hint">
          Your files stay on this computer. Nothing is uploaded except when converting a Word,
          Excel or PowerPoint file, which needs the server.
        </p>

        <h3>Putting pages together</h3>
        <ul className="help__list">
          <li>
            <strong>Add files</strong> — drop them anywhere on the page. Every page appears in the
            grid.
          </li>
          <li>
            <strong>Reorder</strong> — drag a page. Select several first and they move together.
          </li>
          <li>
            <strong>Rotate or delete</strong> — the buttons under each page, or select several and
            use the toolbar.
          </li>
          <li>
            <strong>Blank page</strong> — inserts after the selected page, or at the end.
          </li>
          <li>
            <strong>Add text</strong> — open a page full size, choose <em>Add text</em>, then click
            where you want it. Useful for filling in a form that isn’t fillable, or adding a date.
            Pick the font, size, bold, italic and color before or while you type. It puts new text
            on top and does not change the text already on the page.
          </li>
          <li>
            <strong>Split</strong> — the scissors mark where one file ends and the next begins.
            <em> Split by source file</em> does it automatically. Several files download as a ZIP.
          </li>
        </ul>

        <h3>Page numbers, Bates and watermarks</h3>
        <p>
          These are behind <strong>Export options</strong> in the toolbar, with a preview of the
          real page so you can adjust before saving. They are applied as the file is written, so
          your working copy stays clean.
        </p>
        <ul className="help__list">
          <li>
            <strong>Page numbers</strong> — use <code>{'{n}'}</code> for the page and{' '}
            <code>{'{N}'}</code> for the last one, so <code>Page {'{n}'} of {'{N}'}</code>.
          </li>
          <li>
            <strong>Bates numbering</strong> — a format like <code>ABC-{'{n}'}</code> with a start
            number.
          </li>
          <li>
            <strong>Watermark</strong> — text, the ON Services logo, or the logo with text under
            it. The logo is always upright; plain text can be angled.
          </li>
          <li>
            <strong>Document properties</strong> — title, author, subject and keywords, in the
            Export dialog.
          </li>
        </ul>

        <h3>About the text you add</h3>
        <p>
          Open a page with the magnifier, choose <strong>Add text</strong>, then click where it
          should go. Click a box to choose it: the controls then act on that box, so you can change
          its font, size, color, weight or angle at any time — the <strong>°</strong> field turns
          the text clockwise. <strong>Drag</strong> a box to move it, and remove one with the{' '}
          <strong>×</strong> on it or the <kbd>Delete</kbd> key. Undo takes back any of it.
        </p>
        <p>Two more things worth knowing, because both surprise people:</p>
        <ul className="help__list">
          <li>
            <strong>It is fixed in place when you export.</strong> The text becomes part of the
            page, like ink on paper. Open the exported file again and you can’t move or retype it —
            so get it right before you export, or keep the original and redo it.
          </li>
          <li>
            <strong>Nothing is saved when you close this.</strong> Your pages, their order, any
            rotation and every text box live in this tab only. Close it, reload, or open the app
            somewhere else and you start again from the files.{' '}
            <strong>Export before you finish.</strong>
          </li>
        </ul>

        <h3>What is recorded</h3>
        <p>
          So we can show that this tool is being used, it records that an action happened — that
          you added a file, exported a PDF, deleted a page — along with your name and the time.
        </p>
        <p>
          <strong>It records nothing about the documents themselves.</strong> No filenames, no page
          content, no copies. Those never leave this computer, exactly as before.
        </p>

        <h3>Shortcuts</h3>
        <ul className="help__list help__list--keys">
          <li>
            <kbd>Ctrl</kbd>/<kbd>⌘</kbd> + <kbd>A</kbd> select all
          </li>
          <li>
            <kbd>Delete</kbd> remove selected pages
          </li>
          <li>
            <kbd>[</kbd> <kbd>]</kbd> rotate left and right
          </li>
          <li>
            <kbd>Esc</kbd> clear the selection
          </li>
          <li>
            <kbd>Ctrl</kbd>/<kbd>⌘</kbd> + <kbd>Z</kbd> undo, add <kbd>Shift</kbd> to redo
          </li>
          <li>Shift-click for a range, Ctrl/⌘-click to add one page</li>
          <li>Double-click a page to see it full size</li>
          <li>
            Viewing one page: <kbd>Delete</kbd> removes the text box you have chosen
          </li>
        </ul>

        <h3>What it doesn’t do</h3>
        <p>
          No OCR (making a scan searchable), redaction or form filling. If you need those, email{' '}
          <a href="mailto:support@onservices.com">support@onservices.com</a> so your Adobe license
          is kept.
        </p>
        <p className="dialog__hint" style={{ marginTop: 14 }}>
          Tip: Word opens PDFs directly and converts them to editable documents — File → Open, then
          pick the PDF.
        </p>
      </div>
    </div>
  );
}
